import React, { useState, useEffect } from 'react';
import { CircularProgress, Box } from '@mui/material';
import { applyAllTransformationsForEditing } from '../../utils/EmailTemplateTransformations';

const ReportedSimEmailPreview = ({
    content,
    isLoading,
    colors,
    logoUrl,
    tenancyName,
    scale = 0.6  // Default scale for better fit
}) => {
    const [processedContent, setProcessedContent] = useState('');

    // Sample data for reported sim email placeholders
    const sampleData = {
        firstName: "John",
        lastName: "Doe",
        tenancyName: tenancyName || "Your Organisation",
        simulationId: "SIM123456",
        originalSubject: "Urgent: Account Verification Required",
        fromAddress: "security@suspicious-bank.com",
        reportedAt: new Date().toLocaleString('en-GB'),
        mainText: "You successfully reported a training simulation. Nice work!"
    };

    useEffect(() => {
        if (!content || isLoading) {
            setProcessedContent('');
            return;
        }

        const processTemplate = (htmlContent) => {
            // First apply color and logo transformations
            console.log("Applying transformations with colors:logoUrl:", logoUrl);
            let processed = applyAllTransformationsForEditing(
                htmlContent,
                colors || {
                    primary: '#0b5fff',
                    secondary: '#666666',
                    tertiary: '#cccccc'
                },
                logoUrl
            );

            // Replace all placeholders with sample data
            Object.entries(sampleData).forEach(([key, value]) => {
                const placeholder = `{${key}}`;
                processed = processed.replace(new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'), value);
            });

            // Replace any remaining common placeholders
            processed = processed
                .replace(/\{user\.firstname\}/gi, sampleData.firstName)
                .replace(/\{user\.lastname\}/gi, sampleData.lastName)
                .replace(/\{client\.name\}/gi, sampleData.tenancyName)
                .replace(/\{tenancy\.name\}/gi, sampleData.tenancyName);

            return processed;
        };

        setProcessedContent(processTemplate(content));
    }, [content, isLoading, colors, logoUrl, tenancyName]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    backgroundColor: 'white',
                    flexGrow: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px'
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    if (!content) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '400px',
                    backgroundColor: '#f9f9f9',
                    flexGrow: 1,
                    border: '1px solid #e0e0e0',
                    borderRadius: '8px',
                    color: '#666',
                    fontSize: '14px'
                }}
            >
                No template content available
            </Box>
        );
    }

    return (
        <iframe
            srcDoc={`
                <html>
                    <head>
                        <meta charset="utf-8">
                        <meta name="viewport" content="width=device-width, initial-scale=1">
                        <style>
                            body {
                                margin: 0;
                                /* Use zoom instead of transform scale for proper scrolling */
                                zoom: ${scale};
                                font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
                                background-color: #f5f5f5;
                                overflow-y: auto;
                                overflow-x: hidden;
                            }
                            
                            /* Ensure tables and email content render properly */
                            table {
                                border-collapse: collapse;
                                mso-table-lspace: 0pt;
                                mso-table-rspace: 0pt;
                            }
                            
                            img {
                                border: 0;
                                height: auto;
                                line-height: 100%;
                                outline: none;
                                text-decoration: none;
                                max-width: 100%;
                            }
                            
                            /* Handle any missing styles gracefully */
                            p, div, span {
                                line-height: 1.4;
                            }
                           
                        </style>
                    </head>
                    <body>
                        <div class="email-container">
                            ${processedContent}
                        </div>
                    </body>
                </html>
            `}
            style={{
                border: "1px solid #e0e0e0",
                borderRadius: "8px",
                width: "100%",
                height: "400px",
                backgroundColor: "white",
                flexGrow: 1,
            }}
            title="Reported Sim Email Preview"
            sandbox="allow-same-origin allow-scripts"
            onLoad={(e) => {
                // Prevent navigation and form submissions in preview
                const frame = e.target;
                const frameDoc = frame.contentDocument || frame.contentWindow.document;

                if (!frameDoc) return;

                // Disable all links
                const links = frameDoc.querySelectorAll('a');
                links.forEach(link => {
                    link.onclick = (e) => {
                        e.preventDefault();
                        return false;
                    };
                    // Visual indicator that links are disabled
                    link.style.cursor = 'default';
                    link.title = 'Link disabled in preview';
                });

                // Disable all buttons and form submissions
                const buttons = frameDoc.querySelectorAll('button, input[type="submit"], input[type="button"]');
                buttons.forEach(button => {
                    button.onclick = (e) => {
                        e.preventDefault();
                        return false;
                    };
                    button.style.cursor = 'default';
                    button.title = 'Button disabled in preview';
                });

                // Disable all forms
                const forms = frameDoc.querySelectorAll('form');
                forms.forEach(form => {
                    form.onsubmit = (e) => {
                        e.preventDefault();
                        return false;
                    };
                });
            }}
        />
    );
};

export default ReportedSimEmailPreview;